<template>
    <div class="apprentice-simple-card">
        <div class="row">
            <div class="col-12">
                <div class="form-group mission-name">
                    <p class="h5">{{ $t('quest.assignment_name') }}</p>
                    <b-form-input
                        v-model="mission.name"
                        :placeholder="$t('common.name')"></b-form-input>
                </div>

                <div class="mission-description">
                    <p class="h5">{{ $t('quest.assignment_description') }}</p>
                    <ckeditor
                        v-model="mission.description"
                        :editor="editor"
                        :config="editorConfig"></ckeditor>
                </div>
            </div>

            <div class="col-12">
                <div
                    class="alert alert-danger"
                    v-if="hasErrors"
                    role="alert">
                    {{ $t('quest.skills_can_only_be_a_number_between_0_and_9') }}
                </div>
            </div>

            <div
                class="col-12 col-md-6"
                v-if="mission.quest.skills.length > 0">
                <div class="mission-skills">
                    <p class="h5">{{ $t('quest.skills') }}</p>
                    <div class="skill-list">
                        <div
                            class="skill form-group"
                            v-for="skill in mission.quest.skills">
                            <label class="skill-name">
                                {{ skill.name }}
                            </label>

                            <b-form-input
                                v-model="skill.values[0].value"
                                placeholder="0"
                                type="number">
                            </b-form-input>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-6">
                <div class="mission-settings">
                    <p class="h5">{{ $t('quest.settings_assignment') }}</p>

                    <div class="mission-difficulty form-group">
                        <label>{{ $t('quest.difficulty_50_150') }}</label>
                        <b-form-input
                            v-model="mission.difficulty"
                            placeholder=""
                            type="number"></b-form-input>
                    </div>

                    <div class="form-group mission-group-size">
                        <label>{{ $t('quest.maximum_group_size') }}</label>
                        <b-form-input
                            v-model="mission.group_size"
                            placeholder=""
                            type="number"></b-form-input>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <div class="files-and-links">
                    <h4>{{ $t('quest.files_and_links') }}</h4>

                    <b-tabs
                        class="apprentice-pills-tab"
                        pills
                        vertical
                        nav-wrapper-class="col-12 col-md-3">
                        <b-tab
                            :title="$t('quest.files')"
                            active
                            title-item-class="settings-tab-item"
                            title-link-class="settings-tab-item-link">
                            <template v-slot:title>
                                <i class="fa fa-cloud-upload-alt fa-fw"></i> {{ $t('quest.files') }}
                            </template>
                            <h4 class="card-title">{{ $t('quest.upload_files') }}</h4>
                            <p class="card-description">
                                {{ $t('quest.upload_below_that_you_want_to_attach_to_this_assignment') }}
                            </p>
                            <b-form-file
                                class="apprentice-file-upload quest-files-upload"
                                v-model="files"
                                :state="Boolean(files)"
                                @input="uploadFiles"
                                :placeholder="$t('quest.choose_a_file_or_drag_it_here')"
                                :drop-placeholder="$t('quest.drag_a_file_here')">
                            </b-form-file>

                            <div
                                class="files"
                                v-if="mission.files.length > 0">
                                <div
                                    class="uploaded-file"
                                    v-for="file in mission.files">
                                    <div
                                        class="icon btn-video-icon"
                                        :class="getFileType(file.extension)"></div>
                                    <div class="title">
                                        {{ file.name }}
                                        <div class="file-size text-muted">
                                            {{ file.size }}
                                        </div>
                                    </div>
                                    <div class="actions">
                                        <b-button
                                            class="btn-remove btn-remove-file"
                                            v-b-tooltip.hover
                                            variant="danger-icon-only"
                                            :title="$t('common.delete')"
                                            @click="deleteFile(file.id)">
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </b-tab>

                        <b-tab
                            :title="$t('quest.links')"
                            title-item-class="settings-tab-item"
                            title-link-class="settings-tab-item-link">
                            <template v-slot:title><i class="fa fa-link fa-fw"></i> {{ $t('quest.links') }}</template>
                            <h4 class="card-title">{{ $t('quest.links') }}</h4>
                            <p class="card-description">{{ $t('quest.enter_the_link_to_a_file_or_web_page_below') }}</p>

                            <div class="form-group">
                                <label>{{ $t('common.title') }}</label>
                                <b-form-input
                                    v-model="link_title"
                                    :placeholder="$t('common.title')"></b-form-input>
                            </div>

                            <div class="form-group">
                                <label>{{ $t('quest.link_to_file_or_website') }}</label>
                                <b-form-input
                                    v-model="link_url"
                                    placeholder="Url"></b-form-input>
                            </div>

                            <div class="form-group">
                                <label>&nbsp;</label>
                                <button
                                    class="btn btn-sm btn-primary"
                                    @click="addLink">
                                    {{ $t('common.save') }}
                                </button>
                            </div>

                            <div v-if="mission.links.length > 0">
                                <div
                                    class="link-item"
                                    v-for="link in mission.links">
                                    <div class="link-content">
                                        <div class="icon btn-external"></div>
                                        <div class="link-title">{{ link.name }}</div>
                                        <a
                                            class="clickable"
                                            :href="link.url"
                                            target="_blank"></a>
                                    </div>
                                    <b-button
                                        class="btn-remove btn-remove-link"
                                        v-b-tooltip.hover
                                        variant="danger-icon-only"
                                        :title="$t('common.delete')"
                                        @click="deleteLink(link.id)">
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>
                    </b-tabs>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <hr />
                <button
                    class="btn btn-primary"
                    @click="saveMission">
                    {{ $t('quest.save_assignment') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import ApprenticeEditor from '../../helpers/ApprenticeEditor';

export default {
    name: 'design-2021-quest-edit-missions-component',
    props: ['missionIn'],
    data() {
        //set default skill values
        for (var i = 0; i < this.missionIn.quest.skills.length; i++) {
            if (this.missionIn.quest.skills[i].values.length == 0) {
                this.missionIn.quest.skills[i].values[0] = {};
                this.missionIn.quest.skills[i].values[0].value = '';
            }
        }

        return {
            mission: this.missionIn,
            editor: ApprenticeEditor,
            editorConfig: {
                contentsCss: 'test',
                bodyClass: 'mybodyclass',
                toolbar: {
                    items: [
                        'undo',
                        'redo',
                        '|',
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'link',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'outdent',
                        'indent',
                        'alignment',
                        '|',
                        'insertTable',
                        '|',
                        'fontColor',
                        'fontFamily',
                        'fontSize',
                        'fontBackgroundColor',
                        'highlight',
                        'specialCharacters',
                        '|',
                        'horizontalLine',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
            },
            files: null,
            link_title: '',
            link_url: '',
            hasErrors: false,
        };
    },
    methods: {
        saveMission() {
            const values = {
                mission: this.mission,
            };

            for (const skill of this.mission.quest.skills) {
                if (skill.values[0].value !== null && skill.values[0].value > 9) {
                    this.hasErrors = true;
                    break;
                } else {
                    this.hasErrors = false;
                }
            }

            if (this.hasErrors === false) {
                this.alert.saving(this.$t('quest.saving'), this.$t('quest.assignment_will_be_saved'));

                this.axios
                    .post(this.routes.questEditMissionUpdate(this.mission.quest_id, this.mission.id), values)
                    .then(
                        (response) => {
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
            }
        },
        goToLink(url) {
            window.open(url, '_blank');
        },
        goToFile(url) {
            window.open(url, '_blank');
        },
        addLink() {
            this.alert.saving();

            let data = {
                title: this.link_title,
                url: this.link_url,
                id: this.mission.id,
            };

            this.axios.post(this.routes.questEditMissionLinksAdd(this.mission.quest_id, this.mission.id), data).then(
                (response) => {
                    this.link_title = '';
                    this.link_url = '';
                    this.mission.links = response.data.links;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        deleteLink(id) {
            this.alert
                .confirm(this.$t('quest.delete_link'), this.$t('quest.are_you_sure_you_want_to_delete_this_link'))
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        var data = {
                            id: this.mission.id,
                            link_id: id,
                        };

                        this.axios
                            .post(this.routes.questEditMissionLinksDelete(this.mission.quest_id, this.mission.id), data)
                            .then(
                                (response) => {
                                    this.mission.links = this.mission.links.filter((link) => link.id !== id);
                                    this.alert.success();
                                },
                                (error) => {
                                    this.alert.ajaxError(error);
                                },
                            );
                    }
                });
        },
        deleteFile(id) {
            this.alert
                .confirm(this.$t('quest.delete_file'), this.$t('quest.are_you_sure_you_want_to_delete_this_file'))
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        var data = {
                            id: this.mission.id,
                            file_id: id,
                        };

                        this.axios
                            .post(this.routes.questEditMissionFilesDelete(this.mission.quest_id, this.mission.id), data)
                            .then(
                                (response) => {
                                    this.mission.files = this.mission.files.filter((file) => file.id !== id);
                                    this.alert.success();
                                },
                                (error) => {
                                    this.alert.ajaxError(error);
                                },
                            );
                    }
                });
        },
        uploadFiles() {
            this.alert.saving();

            const headers = { 'Content-Type': 'multipart/form-data' };
            let formData = new FormData();
            formData.append('files', this.files);
            formData.append('id', this.mission.id);

            this.axios
                .post(this.routes.questEditMissionFilesUpload(this.mission.quest_id, this.mission.id), formData, {
                    headers: headers,
                })
                .then(
                    (response) => {
                        this.files = null;
                        this.mission.files = response.data.files;
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
        },
        getFileType(filetype) {
            switch (filetype) {
                case 'jpeg':
                case 'jpg':
                case 'png':
                case 'jif':
                case 'jfif':
                case 'gif':
                    return 'btn-image-type ';

                case 'pdf':
                    return 'btn-file-type ';

                case 'doc':
                case 'docx':
                    return 'btn-doc-type ';

                case 'ppt':
                case 'pptx':
                    return 'btn-doc-type ';

                case 'xls':
                case 'xlx':
                    return 'btn-doc-type ';

                case 'cda':
                case 'mp3':
                case 'oga':
                    return 'btn-audio-type ';

                case 'mov':
                case 'mpeg':
                case 'mkv':
                case 'ogv':
                case 'webm':
                case '3gp':
                case 'mp4':
                case 'mpeg4':
                    return 'btn-video-type ';

                default:
                    return 'btn-file-type ';
            }
        },
    },
};
</script>
<style scoped lang="scss">
.mission-name,
.mission-description {
    margin-bottom: 30px;
}

.mission-skills .h5,
.mission-settings .h5 {
    margin-bottom: 15px;
}

.mission-skills label,
.mission-settings label {
    font-weight: 500;
}

.files-and-links {
    padding-top: 15px;
    margin-top: 21px;
    border-top: 2px solid #eee;
}

.quest-files-upload {
    height: 51px;
}

.files {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
    gap: 0 15px;

    .uploaded-file {
        display: flex;
        justify-content: flex-start;
        align-content: center;
        font-size: 1.125rem;
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        width: 100%;

        .icon {
            align-self: flex-start;
            padding: map-get($grid-gutter-spacing, 'ten');
            background: $secondary-background-color;
            border-radius: $border-radius-content;
            color: var(--primary);

            &:before {
                margin-right: 0;
            }
        }

        .title {
            font-size: $default-font-size;
            font-weight: $font-weight-bold;
            margin-left: map-get($grid-gutter-spacing, 'fifteen');
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
            align-self: center;
            word-break: break-word;
        }

        .actions {
            display: flex;
            align-items: center;
            align-self: flex-start;
            margin-left: auto;

            .btn {
                padding-top: 4px;
            }
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
            padding-bottom: map-get($grid-gutter-spacing, 'eight');
        }
    }
}

.link-item {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-content: center;
    font-size: $base-font-size;
    border: 0;
    border-radius: $border-radius-content;
    padding: map-get($grid-gutter-spacing, 'eight') map-get($grid-gutter-spacing, 'twenty-one')
        map-get($grid-gutter-spacing, 'eight') 0;

    .link-content {
        display: flex;
        flex-direction: row;
    }

    .icon {
        align-self: flex-start;
        padding: map-get($grid-gutter-spacing, 'ten');
        background: $secondary-background-color;
        border-radius: $border-radius-content;
        color: var(--primary);

        &:before {
            margin-right: 0;
        }
    }

    .link-title {
        font-size: $default-font-size;
        align-self: center;
        margin-left: map-get($grid-gutter-spacing, 'fifteen');
        color: $text-muted-color;
        font-weight: $font-weight-medium;
    }

    .btn-remove-link {
        align-self: center;
    }

    &:hover {
        background-color: $secondary-background-color;
        border-radius: $border-radius-content;
    }

    &:not(:last-child) {
        border-bottom: 3px dotted $border-color;
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .files {
        .uploaded-file {
            width: calc(33.3% - 15px);
        }
    }

    .link-item {
        max-width: 50%;
    }

    /* Safari Only media Queries */
    _::-webkit-full-page-media,
    _:future,
    :root {
        .files {
            .uploaded-file {
            }
        }
    }

    @media not all and (min-resolution: 0.001dpcm) {
        .files {
            .uploaded-file {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }
        }
    }
}
</style>
